import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface ErrorLogParams {
  error: Error | string;
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

export async function logError({
  error,
  component,
  action,
  metadata,
}: ErrorLogParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    // Use type assertion for the table that was just created
    const { error: insertError } = await (supabase as any)
      .from("error_logs")
      .insert({
        error_message: errorMessage,
        error_details: typeof error === "object" ? JSON.stringify(error) : null,
        error_stack: errorStack || null,
        user_id: user?.id || null,
        user_email: user?.email || null,
        page_url: window.location.href,
        component: component || null,
        action: action || null,
        metadata: (metadata as Json) || null,
      });

    if (insertError) {
      console.error("Failed to log error to database:", insertError);
    }
  } catch (e) {
    // Don't throw if logging fails - just log to console
    console.error("Error logging failed:", e);
  }
}

// Convenience function for catching errors in async operations
export function withErrorLogging<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  component?: string,
  action?: string
): T {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      await logError({
        error: error instanceof Error ? error : String(error),
        component,
        action,
      });
      throw error;
    }
  }) as T;
}
